import type { SymbolRemovalAnimationRequest, SymbolRemovalEffect } from "@slot/presentation";

/**
 * This game's symbol removal: every removed cell plays `fallback` (its Spine `out`; CH and FS carry none, so it declines
 * and the mechanic shrinks the cell with a burst) — except the detonating chance barrel. The barrel's `win` clip is
 * already its explosion and ends with it gone, so for the length of the detonation's removal its cell plays nothing:
 * the removal releases it when that clip ends.
 */
export class ChanceRemovalEffect implements SymbolRemovalEffect {
  private readonly fallback: SymbolRemovalEffect | null;
  /** The detonating barrel's cell and the end of its clip, while the detonation's removal runs. */
  private barrel: { index: number; clipEnd: Promise<void> } | null = null;

  public constructor(fallback: SymbolRemovalEffect | null) {
    this.fallback = fallback;
  }

  /** Runs `remove` with the cell at `index` leaving when `clipEnd` resolves, instead of through a removal of its own. */
  public async withBarrelExit(index: number, clipEnd: Promise<void>, remove: () => Promise<void>): Promise<void> {
    this.barrel = { index, clipEnd };
    try {
      await remove();
    } finally {
      this.barrel = null;
    }
  }

  public play(request: SymbolRemovalAnimationRequest): Promise<void> | null {
    if (this.barrel?.index === request.index) return this.barrel.clipEnd;
    return this.fallback?.play(request) ?? null;
  }
}
